import React, { useEffect } from "react";
import { useTheme } from "@mui/material/styles";
import {
  SelectProps as MuiSelectFieldProps,
  ClickAwayListener,
  AutocompleteCloseReason,
  createFilterOptions,
  SxProps,
} from "@mui/material";

import {
  StyledAutocompletePopper,
  StyledPopper,
  TypographyPopper,
  OptionsContainer,
  StyledAutocomplete,
  ButtonContainer,
} from "./SelectWithTags.styles";
import { Button, SelectField, Tag, TextField } from "..";
import { FieldLengths, otherAccessibilityCounter } from "src/models/FieldLengths";

interface PopperComponentProps {
  anchorEl?: any;
  disablePortal?: boolean;
  open: boolean;
}

interface OptionItem {
  value: string;
  label: string;
  manualInput?: boolean;
}

function PopperComponent(props: PopperComponentProps) {
  const { disablePortal, anchorEl, open, ...other } = props;
  return <StyledAutocompletePopper {...other} />;
}

function areEqual(optionA: OptionItem, optionB: OptionItem) {
  return optionA.value === optionB.value;
}

const filter = createFilterOptions<OptionItem>();

function initialValueMapping(value: string | string[], other: string | string[], options: { [key: string]: string }) {
  if (value === "") return [];

  let valueMapping = value;
  let initialValue: OptionItem[];
  if (other !== null && other !== "") {
    valueMapping = other;

    initialValue = Array.isArray(valueMapping)
      ? valueMapping.map((item: string) => ({ value: item, label: item } as OptionItem))
      : [({ value: valueMapping, label: valueMapping } as OptionItem)]
  } else {
    initialValue = Array.isArray(valueMapping)
      ? valueMapping.map((item: string) => ({ value: item, label: options[item] } as OptionItem))
      : [({ value: valueMapping, label: options[valueMapping] } as OptionItem)]
  }

  return initialValue;
}

export interface CustomSelectWithTagsProps
  extends Pick<MuiSelectFieldProps, "id" | "fullWidth" | "sx"> {
  variant?: "primary" | "secondary";
  size?: "sm" | "md" | "lg";
  value: string | string[];
  other?: string | string[] | null;
  label?: string;
  placeholder?: string;
  error?: boolean;
  errorMessage?: string;
  errorBackground?: boolean;
  disabled?: boolean;
  creatable?: boolean;
  multiple?: boolean;
  doneButton?: boolean;
  leftIcon?: React.ReactElement | null;
  disabledIcon?: React.ReactElement | null;
  options: { [key: string]: string };
  customInputLabelSx?: SxProps
  onChange: (value: OptionItem | OptionItem[] | undefined) => void;
  onBlurCapture?: () => void;
}

export const SelectWithTags = ({
  variant = "secondary",
  size = "md",
  label,
  placeholder,
  options,
  multiple = false,
  creatable = false,
  doneButton = false,
  error = false,
  disabled = false,
  disabledIcon = null,
  leftIcon = null,
  errorMessage,
  errorBackground = false,
  value,
  other,
  onChange,
  onBlurCapture,
  customInputLabelSx,
  sx,
  ...rest
}: CustomSelectWithTagsProps) => {
  const initialValue = initialValueMapping(value, other || "", options);
  const initialOtherValues = other ? initialValue : [];

  const [otherValues, setOtherValues] = React.useState<OptionItem[]>(initialOtherValues);
  const [localValue, setLocalValue] = React.useState<OptionItem[]>(initialValue);
  const [pendingValue, setPendingValue] = React.useState<OptionItem[]>([]);

  const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);
  const [inputValue, setInputValue] = React.useState("");
  const [openSelect, setOpenSelect] = React.useState(false);

  const theme = useTheme();

  useEffect(() => {
    const initialValue = initialValueMapping(value, other || "", options);
    const initialOtherValues = other ? initialValue : [];
    setLocalValue(initialValue)
    setOtherValues(initialOtherValues)
  }, [value, other])

  const optionsArray: OptionItem[] = Object.entries(options)
    .map(([value, label]) => ({ value, label }));

  const handleClick = (event: React.MouseEvent<HTMLElement>) => {
    setPendingValue(localValue);
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setOpenSelect(false);
    setLocalValue(pendingValue);
    onChange(pendingValue);

    if (onBlurCapture) {
      onBlurCapture();
    }

    if (anchorEl) {
      anchorEl.focus();
    }
    setAnchorEl(null);
  };

  const openPopper = Boolean(anchorEl);
  const id = openPopper ? "selectWithLabelPopper" : undefined;

  return (
    <React.Fragment>
      <SelectField
        data-cy="autoComplete-select"
        aria-describedby={id}
        multiple
        variant={variant}
        size={size}
        label={label}
        placeholder={placeholder}
        open={openSelect}
        inputProps={{ readOnly: true }}
        value={localValue.map((item) => item.label)}
        options={{ [""]: false }}
        onClick={disabled ? () => { } : handleClick}
        onOpen={() => setOpenSelect(true)}
        onClose={() => setOpenSelect(false)}
        onChange={() => { }}
        MenuProps={{ open: false }}
        disabledIcon={disabledIcon}
        disabled={disabled}
        sx={{ ...sx }}
        error={error}
        errorMessage={errorMessage}
        customInputLabelSx={customInputLabelSx}
        errorBackground={errorBackground}
        {...rest}
      />
      <StyledPopper
        id={id}
        open={openPopper}
        anchorEl={anchorEl}
        popperOptions={{
          placement: "bottom-start",
          modifiers: [
            {
              name: "preventOverflow",
              options: {
                rootBoundary: "viewport",
                boundary: "viewport",
                padding: 30,
                altAxis: true,
              },
            },
          ],
        }}
      >
        <ClickAwayListener onClickAway={handleClose}>
          <OptionsContainer>
            <TypographyPopper>Please select an option below: </TypographyPopper>
            <StyledAutocomplete
              disablePortal
              data-cy="autoComplete-input"
              open
              multiple
              value={pendingValue}
              inputValue={inputValue}
              openOnFocus
              selectOnFocus
              clearOnBlur
              handleHomeEndKeys
              includeInputInList
              disableClearable
              disableCloseOnSelect
              disableListWrap
              forcePopupIcon={false}
              onChange={(event, newValue, reason, details) => {
                let newPendingValue: OptionItem[];
                if (multiple) {
                  newPendingValue = newValue
                } else {
                  if (reason === "selectOption" && details?.option) {
                    newPendingValue = [details.option];
                  }

                  if (reason === "removeOption") {
                    newPendingValue = [];
                  }
                }
                setPendingValue(newPendingValue!);
              }}
              onInputChange={(event, newInputValue) => {
                setInputValue(newInputValue);
              }}
              onClose={(
                event: React.ChangeEvent<{}>,
                reason: AutocompleteCloseReason
              ) => {
                if (reason === "escape") {
                  handleClose();
                }
              }}
              filterOptions={(options, params) => {
                params.inputValue = params.inputValue.trim();
                const filtered = filter(options, params);
                return filtered;
              }}
              PopperComponent={PopperComponent}
              renderTags={() => null}
              noOptionsText={
                <TypographyPopper>
                  Option not found. Please press enter to add
                </TypographyPopper>
              }
              isOptionEqualToValue={areEqual}
              renderOption={(props, option, state) => {
                return (
                  <li {...props}>
                    <Tag
                      data-cy={`select-item-${option.label}`}
                      isActive={state.selected}
                      onDelete={() => {
                        const updatedPendingValue = pendingValue.filter(
                          (stateOption) => stateOption.value !== option.value
                        );
                        setPendingValue(updatedPendingValue);
                      }}
                      isControlled
                      label={option.label}
                    />
                  </li>
                );
              }}
              options={[...optionsArray, ...otherValues]}
              getOptionLabel={(option) => {
                if (typeof option === "string") {
                  return option;
                }
                if (option.label) {
                  return option.value;
                }
                return option.label;
              }}
              renderInput={(params) => (
                <TextField
                  variant="secondary"
                  inputProps={{ ...params.inputProps, maxLength: FieldLengths.user.otherAccessibility }}
                  inputRef={params.InputProps.ref}
                  helperText={otherAccessibilityCounter(inputValue)}
                  placeholder="Please Specify"
                  size="md"
                  label="Other"
                  onKeyDown={(e) => {
                    if (e.key === "Enter") {
                      if (inputValue) {
                        const isExisting = [
                          ...optionsArray,
                          ...otherValues,
                        ].some((option) => inputValue.trim() === option.label);
                        if (!isExisting) {
                          const optionObj = {
                            value: inputValue,
                            label: inputValue,
                            manualInput: true,
                          } as OptionItem;

                          setPendingValue(multiple ? (current) => [...current, optionObj] : [optionObj]);
                          setOtherValues((current) => [...current, optionObj]);
                          setInputValue("");
                        }
                      }
                    }
                  }}
                />
              )}
            />
            {doneButton && (
              <ButtonContainer>
                <Button data-cy="select-option-button" onClick={handleClose} full>
                  Done
                </Button>
              </ButtonContainer>
            )}
          </OptionsContainer>
        </ClickAwayListener>
      </StyledPopper>
    </React.Fragment>
  );
};

export interface SelectWithTagsProps
  extends React.ComponentProps<typeof SelectWithTags> {
  as?: React.ElementType;
}

SelectWithTags.displayName = "SelectWithTags";
